import { Box, Chip, Grid, List, ListItem, ListItemText, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import type React from 'react';
import type { ActivityData, ChartData } from './types';

const EMPTY_OBJECT: Record<string, never> = {};

interface LoginsTabProps {
  data: ActivityData;
  chartData: ChartData;
}

export const LoginsTab: React.FC<LoginsTabProps> = ({ data, chartData }) => {
  if (!data.loginAnalytics) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ pl: 2 }}
            data-testid="login-distribution-chart"
          >
            Login Distribution by Hour
          </Typography>
          {chartData.hourlyLoginData?.some((d) => d.logins > 0) ? (
            <Box sx={{ height: 300 }}>
              <BarChart
                dataset={chartData.hourlyLoginData}
                xAxis={[{ dataKey: 'hour', scaleType: 'band' }]}
                series={[{ dataKey: 'logins', label: 'Logins', color: '#82ca9d' }]}
                height={280}
              />
            </Box>
          ) : (
            <Typography color="text.primary" sx={{ mt: 2 }}>
              No login data available for this period.
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" gutterBottom sx={{ pl: 2 }}>
            Device Types
          </Typography>
          {Object.keys(data.loginAnalytics.deviceBreakdown || EMPTY_OBJECT).length > 0 ? (
            <List>
              {Object.entries(data.loginAnalytics.deviceBreakdown).map(([device, count]) => (
                <ListItem key={device}>
                  <ListItemText primary={device} secondary={`${count} logins`} />
                  <Chip label={count} color="primary" size="small" />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.primary" sx={{ mt: 2, pl: 2 }}>
              No device data.
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" gutterBottom sx={{ pl: 2 }}>
            Daily Login Trend
          </Typography>
          {chartData.dailyLoginData.length > 0 ? (
            <List>
              {chartData.dailyLoginData.map(({ date, count }) => (
                <ListItem key={date}>
                  <ListItemText
                    primary={new Date(date).toLocaleDateString()}
                    secondary={`${count} logins`}
                  />
                  <Chip label={count} color="success" size="small" />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.primary" sx={{ mt: 2, pl: 2 }}>
              No daily login data.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
